import React, { useState } from 'react';
import { useColorPalette } from '../contexts/ColorPaletteContext';
import CustomPaletteEditor from './CustomPaletteEditor';
import { DEFAULT_COLOR_PALETTES } from '../config/colorPalettes';
import styles from '../styles/components/ColorPaletteSelector.module.scss';

const ColorPaletteSelector = () => {
    const {
        currentPalette,
        defaultPalette,
        availablePalettes,
        changePalette,
        saveDefaultPalette,
        saveCustomPalette,
        updateCustomPalette,
        deleteCustomPalette,
        isLoading,
        error
    } = useColorPalette();

    const [isChanging, setIsChanging] = useState(false);
    const [changeStatus, setChangeStatus] = useState('');
    const [showCustomEditor, setShowCustomEditor] = useState(false);
    const [editingPalette, setEditingPalette] = useState(null);
    const [basePalette, setBasePalette] = useState('default');

    const handlePaletteChange = async (paletteKey) => {
        if (paletteKey === currentPalette || isChanging) return;

        try {
            setIsChanging(true);
            setChangeStatus('変更中...');

            await changePalette(paletteKey);

            setChangeStatus('変更しました');
            setTimeout(() => setChangeStatus(''), 2000);
        } catch (err) {
            setChangeStatus('変更に失敗しました');
            setTimeout(() => setChangeStatus(''), 3000);
        } finally {
            setIsChanging(false);
        }
    };

    const handleCreateCustom = (baseKey = 'default') => {
        setBasePalette(baseKey);
        setEditingPalette(null);
        setShowCustomEditor(true);
    };

    const handleEditCustom = (palette) => {
        if (palette.isCustom) {
            // カスタムパレットの場合は既存の編集
            setEditingPalette(palette);
            setBasePalette(null);
        } else {
            // プリセットパレットの場合は新しいカスタムパレットとして編集
            setEditingPalette(null);
            setBasePalette(palette.key);
        }
        setShowCustomEditor(true);
    };

    const handleSaveCustomPalette = async (paletteData) => {
        try {
            if (editingPalette) {
                await updateCustomPalette(editingPalette.key, paletteData);
                setChangeStatus('パレットを更新しました');
            } else {
                const paletteId = await saveCustomPalette(paletteData);
                setChangeStatus('カスタムパレットを作成しました');

                // 作成したパレットを自動的に適用
                await changePalette(paletteId);
            }

            setTimeout(() => setChangeStatus(''), 3000);
        } catch (err) {
            setChangeStatus('保存に失敗しました');
            setTimeout(() => setChangeStatus(''), 3000);
        }
    };

    const handleDeleteCustomPalette = async (paletteKey) => {
        if (!window.confirm('このカスタムパレットを削除してもよろしいですか？')) {
            return;
        }

        try {
            await deleteCustomPalette(paletteKey);
            setChangeStatus('パレットを削除しました');
            setTimeout(() => setChangeStatus(''), 3000);
        } catch (err) {
            setChangeStatus('削除に失敗しました');
            setTimeout(() => setChangeStatus(''), 3000);
        }
    };

    const handleSetDefault = async (paletteKey) => {
        if (paletteKey === defaultPalette) return;

        try {
            setIsChanging(true);
            setChangeStatus('デフォルト設定中...');

            await saveDefaultPalette(paletteKey);

            setChangeStatus('デフォルトパレットを設定しました');
            setTimeout(() => setChangeStatus(''), 3000);
        } catch (err) {
            setChangeStatus('デフォルト設定に失敗しました');
            setTimeout(() => setChangeStatus(''), 3000);
        } finally {
            setIsChanging(false);
        }
    };

    if (isLoading) {
        return (
            <div className={styles.container}>
                <h3>カラーパレット</h3>
                <div className={styles.loading}>読み込み中...</div>
            </div>
        );
    }

    return (
        <>
            <div className={styles.container}>
                <h3>カラーパレット</h3>
                <p className={styles.description}>
                    ウェブサイトで使用するカラーパレットを選択してください。
                    選択したパレットは全てのページに適用されます。
                </p>


                <div className={styles.actions}>
                    <button
                        className={styles.createButton}
                        onClick={() => handleCreateCustom()}
                        disabled={isChanging}
                    >
                        + カスタムパレットを作成
                    </button>
                </div>

                {error && (
                    <div className={styles.error}>
                        {error}
                    </div>
                )}

                <div className={styles.paletteGrid}>
                    {availablePalettes.map((palette) => (
                        <div
                            key={palette.key}
                            className={`${styles.paletteCard} ${currentPalette === palette.key ? styles.selected : ''
                                } ${isChanging ? styles.disabled : ''}`}
                            onClick={() => handlePaletteChange(palette.key)}
                        >
                            <div className={styles.paletteHeader}>
                                <div className={styles.paletteTitle}>
                                    <h4>{palette.name}</h4>
                                    {palette.isCustom && (
                                        <span className={styles.customLabel}>カスタム</span>
                                    )}
                                </div>
                                <div className={styles.paletteActions}>
                                    {currentPalette === palette.key && (
                                        <span className={styles.currentLabel}>現在選択中</span>
                                    )}

                                    <div className={styles.actionButtons}>
                                        <button
                                            className={styles.defaultButton}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSetDefault(palette.key);
                                            }}
                                            disabled={isChanging || defaultPalette === palette.key}
                                            title="デフォルトパレットに設定"
                                        >
                                            デフォルト設定
                                        </button>

                                        <button
                                            className={styles.editButton}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEditCustom(palette);
                                            }}
                                            disabled={isChanging}
                                            title="編集"
                                        >
                                            編集
                                        </button>

                                        {palette.isCustom && (
                                            <button
                                                className={styles.deleteButton}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteCustomPalette(palette.key);
                                                }}
                                                disabled={isChanging}
                                                title="削除"
                                            >
                                                削除
                                            </button>
                                        )}

                                        {!palette.isCustom && (
                                            <button
                                                className={styles.copyButton}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCreateCustom(palette.key);
                                                }}
                                                disabled={isChanging}
                                                title="このパレットをベースにカスタムパレットを作成"
                                            >
                                                コピー
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className={styles.colorPreview}>
                                {Object.entries(palette.colors).map(([colorName, colorValue]) => (
                                    <div
                                        key={colorName}
                                        className={styles.colorSwatch}
                                        style={{ backgroundColor: colorValue }}
                                        title={`${colorName}: ${colorValue}`}
                                    />
                                ))}
                            </div>

                            <div className={styles.colorLabels}>
                                <div className={styles.primaryColors}>
                                    <span
                                        className={styles.colorLabel}
                                        style={{ backgroundColor: palette.colors.primary }}
                                    >
                                        Primary
                                    </span>
                                    <span
                                        className={styles.colorLabel}
                                        style={{ backgroundColor: palette.colors.secondary }}
                                    >
                                        Secondary
                                    </span>
                                    <span
                                        className={styles.colorLabel}
                                        style={{ backgroundColor: palette.colors.accent }}
                                    >
                                        Accent
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {changeStatus && (
                    <div className={`${styles.status} ${changeStatus.includes('失敗') ? styles.statusError : styles.statusSuccess
                        }`}>
                        {changeStatus}
                    </div>
                )}

                <div className={styles.previewSection}>
                    <h4>プレビュー</h4>
                    <div className={styles.previewContainer}>
                        <div
                            className={styles.previewCard}
                            style={{
                                backgroundColor: availablePalettes.find(p => p.key === currentPalette)?.colors.background,
                                color: availablePalettes.find(p => p.key === currentPalette)?.colors.text,
                                border: `1px solid ${availablePalettes.find(p => p.key === currentPalette)?.colors.border}`
                            }}
                        >
                            <h5 style={{ color: availablePalettes.find(p => p.key === currentPalette)?.colors.primary }}>
                                サンプルタイトル
                            </h5>
                            <p style={{ color: availablePalettes.find(p => p.key === currentPalette)?.colors.textMuted }}>
                                これはサンプルテキストです。選択したカラーパレットがどのように表示されるかを確認できます。
                            </p>
                            <button
                                className={styles.previewButton}
                                style={{
                                    backgroundColor: availablePalettes.find(p => p.key === currentPalette)?.colors.primary,
                                    color: availablePalettes.find(p => p.key === currentPalette)?.colors.background
                                }}
                            >
                                サンプルボタン
                            </button>
                            <button
                                className={styles.previewButtonSecondary}
                                style={{
                                    backgroundColor: availablePalettes.find(p => p.key === currentPalette)?.colors.accent,
                                    color: availablePalettes.find(p => p.key === currentPalette)?.colors.background
                                }}
                            >
                                アクセントボタン
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* カスタムパレットエディター */}
            <CustomPaletteEditor
                isOpen={showCustomEditor}
                onClose={() => {
                    setShowCustomEditor(false);
                    setEditingPalette(null);
                }}
                onSave={handleSaveCustomPalette}
                initialPalette={editingPalette}
                basePalette={basePalette}
            />
        </>
    );
};

export default ColorPaletteSelector;
